import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import keycloakInstance from "@/initKeycloak";
import { diagnosticEventLog } from "@/shared/services/diagnostic";

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

// Mutex to prevent concurrent refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

/**
 * Axios response interceptor that catches 401 errors, attempts a Keycloak
 * token refresh, and retries the original request with the new token.
 *
 * Key design decisions:
 * - No logout — if refresh fails, the original 401 propagates. The periodic
 *   refresh in useKeycloakTokenHandler or onAuthRefreshError handles logout.
 * - Mutex — concurrent 401s coalesce into a single refresh call.
 * - Single retry — _retried flag prevents infinite loops.
 */
export function attachRetryOn401(client: AxiosInstance): void {
  client.interceptors.response.use(undefined, async (error: AxiosError) => {
    const config = error.config as RetryableConfig | undefined;

    // Only handle 401, only retry once, must have config to retry
    if (error.response?.status !== 401 || !config || config._retried) {
      return Promise.reject(error);
    }

    if (!keycloakInstance.authenticated) {
      return Promise.reject(error);
    }

    // Coalesce concurrent refreshes — only one in-flight at a time
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = keycloakInstance
        .updateToken(5)
        .then((refreshed) => {
          diagnosticEventLog.record("auth:interceptor", "info", "token-refreshed", {
            refreshed,
          });
          return true;
        })
        .catch(() => {
          diagnosticEventLog.record("auth:interceptor", "warn", "token-refresh-failed");
          return false;
        })
        .finally(() => {
          isRefreshing = false;
          refreshPromise = null;
        });
    }

    const refreshed = await refreshPromise;
    if (!refreshed) {
      return Promise.reject(error);
    }

    // Retry with fresh token
    config._retried = true;
    config.headers.Authorization = `Bearer ${keycloakInstance.token}`;
    return client(config);
  });
}
